import React, { useEffect, useMemo, useState } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { Avatar, Badge, Collapse, Tooltip, useMediaQuery } from '@mui/material'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { tooltipClasses } from '@mui/material/Tooltip'
import SplitscreenOutlinedIcon from '@mui/icons-material/SplitscreenOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined'
import InsertChartOutlinedOutlinedIcon from '@mui/icons-material/InsertChartOutlinedOutlined'

import {
  CallSplitOutlined,
  PowerSettingsNewOutlined,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material'
import { makeStyles } from '@mui/styles'
import { useLogout } from '../../hooks/useLogout'
import { useAuthContext } from '../../hooks/useAuthContext'
import { customReportingViewsService } from '../../services/customReportingViewsService'
import { processDataService } from '../../services/processDataService'
import { useCustomTheme } from '../../context/ThemeContext'

const drawerWidthOpened = 240
const drawerWidthClosed = 60

const openedMixin = (theme) => ({
  width: drawerWidthOpened,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}))

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: open ? drawerWidthOpened : drawerWidthClosed,
    width: `calc(100% - ${open ? drawerWidthOpened : drawerWidthClosed}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => {
  return {
    width: open ? drawerWidthOpened : drawerWidthClosed,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    sx: {
      root: {
        width: open ? drawerWidthOpened : drawerWidthClosed,
      },
    },
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }
})

const useStyles = makeStyles((theme) => {
  return {
    page: {
      background: '#fafafa',
      width: '100%',
      padding: theme.spacing(3),
      overflow: 'hidden',
    },
    root: {
      display: 'flex',
    },
    drawer: {
      width: drawerWidthOpened,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      '&.closed': {
        width: '0px',
      },
    },
    drawerPaper: {
      width: drawerWidthOpened,
      '&.closed': {
        width: '0px',
      },
    },
    active: {
      background: '#e6e6fa80',
    },
    title: {
      padding: theme.spacing(2),
    },
    appBar: {
      background: '#fff',
    },
    date: {
      flexGrow: 1,
    },
    toolbar: theme.mixins.toolbar,
    avatar: {
      marginLeft: theme.spacing(2),
    },
  }
})

export default function Layout({ children }) {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useLogout()
  const { user } = useAuthContext()
  const { setTheme } = useCustomTheme()
  const companyId = 10
  const theme = useTheme()
  const [open, setOpen] = React.useState(true)
  const [badgeCounts, setBadgeCounts] = React.useState([])
  const [openMenus, setOpenMenus] = useState({})
  const [hierarchyView, setHierarchyView] = useState({})
  const [customData, setCustomData] = useState([])
  const [activeItem, setActiveItem] = useState('') // State to track active item

  // Media query hook to check if the viewport width is greater than 600px
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'))

  const buildTree = (data) => {
    const tree = []

    data.forEach((item) => {
      const parts = item.path.split('/')
      let currentLevel = tree
      let accumulatedPath = ''

      parts.forEach((part, index) => {
        accumulatedPath += `/${part}`
        let existingNode = currentLevel.find((node) => node.name === part)

        if (!existingNode) {
          existingNode = {
            name: part,
            path: accumulatedPath,
            children: [],
          }
          currentLevel.push(existingNode)
        }

        if (index === parts.length - 1) {
          // Leaf node (actual view)
          existingNode.id = item.id
          existingNode.access = item.access
          existingNode.company_id = item.company_id
        }

        currentLevel = existingNode.children
      })
    })

    return tree
  }

  useEffect(() => {
    if (user) {
      setTheme(user?.company_metadata?.primary_color, user?.company_metadata?.secondary_color)
    }
  }, [user])

  useEffect(() => {
    if (!isLargeScreen) {
      setOpen(false)
    }
  }, [isLargeScreen])

  const handleToggle = (key) => {
    setOpenMenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  useEffect(() => {
    // Update activeItem when the location.pathname changes
    const currentPath = location.pathname
    setActiveItem(currentPath)
  }, [location.pathname])

  const treeData = useMemo(() => buildTree(customData), [customData])

  // Check if user has access to any customData
  const userHasAccess = customData.some(
    (data) =>
      data.is_readonly_access ||
      data.access.owners.includes(user?.id) ||
      data.access.editors.includes(user?.id) ||
      data.access.viewers.includes(user?.id)
  )

  const menuItems = user
    ? [
        {
          text: 'My Tasks',
          icon: <SplitscreenOutlinedIcon color="info" className="fontsize20" />,
          path: '/tasks',
          altPaths: ['/process-data/'],
          isBadgeRequired: true,
        },

        {
          text: 'My Processes',
          icon: <CallSplitOutlined color="info" className="fontsize20" />,
          path: '/my-processes',
          altPaths: ['/process-dashboard/'],
          isBadgeRequired: false,
        },
        ...(userHasAccess && hierarchyView !== null
          ? [
              {
                text: 'Views',
                icon: <FactCheckOutlinedIcon color="info" className="fontsize20" />,
                path: '/my-views/path1',
                altPaths: ['/process-dashboard/'],
                isBadgeRequired: false,
                hasSubMenu: true,
                subMenu: treeData,
              },
            ]
          : []),
        {
          text: 'My Queries',
          icon: <QuizOutlinedIcon color="info" className="fontsize20" />,

          path: '/my-queries',
          altPaths: [],
          isBadgeRequired: true,
        },
        {
          text: 'Admin',
          icon: <AdminPanelSettingsOutlinedIcon color="info" className="fontsize20" />,
          path: '/admin',
          altPaths: [],
          isBadgeRequired: true,
          isAdminRoute: true,
        },
      ]
    : []

  const hasAccess = (node) => {
    const customDataEntry = customData.find((data) => data.id === node.id)

    if (!customDataEntry || !node.id) return false

    return (
      customDataEntry.is_readonly_access ||
      customDataEntry.access.owners.includes(user.id) ||
      customDataEntry.access.editors.includes(user.id) ||
      customDataEntry.access.viewers.includes(user.id)
    )
  }

  const hasAccessibleChildren = (node) => {
    if (!node.children || node.children.length === 0) return false

    return node.children.some((child) => hasAccess(child) || hasAccessibleChildren(child))
  }

  const renderTree = (nodes, parentKey = 'root') => {
    return nodes
      .filter((node) => hasAccess(node) || hasAccessibleChildren(node))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((node) => {
        const key = node.path
        const isOpen = openMenus[key] || false
        const hasChildren = node.children && node.children.length > 0

        return (
          <React.Fragment key={key}>
            <ListItem
              onClick={() => {
                if (hasChildren) {
                  handleToggle(key)
                } else if (node.id) {
                  navigate(`/my-views/${node.id}`)
                }
              }}
              style={{
                paddingLeft: parentKey === 'root' ? 28 : 40,
                backgroundColor:
                  location.pathname == `/my-views/${node.id}` ? '#aeadfa' : 'inherit',
              }}
            >
              <Tooltip title={node.name || `View ${node.path}`} placement="bottom">
                <ListItemIcon className="menulist">
                  {hasChildren ? (
                    <DashboardCustomizeOutlinedIcon style={{ fontSize: '19px' }} />
                  ) : (
                    <InsertChartOutlinedOutlinedIcon style={{ fontSize: '19px' }} />
                  )}
                </ListItemIcon>
              </Tooltip>
              <ListItemText primary={node.name || `View ${node.path}`} />
              {hasChildren ? isOpen ? <ExpandLess /> : <ExpandMore /> : null}
            </ListItem>
            {hasChildren && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div">{renderTree(node.children, key)}</List>
              </Collapse>
            )}
          </React.Fragment>
        )
      })
  }

  const fetchBadgeData = async () => {
    let badgeCount = await processDataService.getBadgeData(user)
    let badgesCountMenuItems = menuItems.map((item) => {
      if (item.text === 'My Tasks') {
        return {
          text: item.text,
          count: parseInt(badgeCount.taskCount) || 0,
        }
      } else if (item.text === 'My Queries') {
        return {
          text: item.text,
          count: parseInt(badgeCount.queryCount) || 0,
        }
      }
    })
    setBadgeCounts(badgesCountMenuItems)
  }

  useEffect(() => {
    if (user) {
      const fetchCustomData = async () => {
        const getCustomReportingViewData =
          await customReportingViewsService.getCustomReportingViewData(user)
        setCustomData(getCustomReportingViewData)
      }
      fetchCustomData()
    }
  }, [user])

  useEffect(() => {
    if (user?.id) {
      fetchBadgeData()
      const fetchBadgeDataInEvery10sec = setInterval(fetchBadgeData, 20000)
      return () => {
        clearInterval(fetchBadgeDataInEvery10sec)
      }
    }
  }, [user])

  useEffect(() => {
    if (user) {
      const fetchHierarchyData = async () => {
        const getCustomReportingViewData = await customReportingViewsService.getCustomHierarchyData(
          user,
          companyId
        )
        setHierarchyView(getCustomReportingViewData)
      }
      fetchHierarchyData()
    }
  }, [user])

  const handleBadgeCount = (menuItem) => {
    let count = 0
    badgeCounts.forEach((item) => {
      if (item?.text == menuItem) {
        count = item?.count
      }
    })
    return count
  }

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <AppBar
        position="fixed"
        className={classes.appBar}
        elevation={0}
        open={isLargeScreen && open}
        sx={{
          boxShadow: '1px 1px #d3d3d3 !important',
          padding: '0px !important',
          width: `calc(100% - ${open ? drawerWidthOpened : drawerWidthClosed}) `,
          backgroundColor: 'white !important',
        }}
      >
        <Toolbar
          style={{
            width: '100% ',
            display: 'flex ',
            alignItems: 'center',
            justifyContent: 'space-between ',
            margin: 0,
            padding: 0,
          }}
        >
          <Box
            style={{
              display: 'flex ',
              alignItems: 'center ',
              margin: 0,
              padding: 0,
            }}
          >
            {!open && isLargeScreen ? (
              <img
                src="/branding/e7_logo_black_round.png"
                alt="Logo"
                onClick={() => navigate('/tasks')}
                style={{ cursor: 'pointer', height: '30px', paddingLeft: '15px' }}
              />
            ) : null}

            <IconButton
              className="drawer-icon"
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(true)}
              edge="start"
              sx={{
                marginRight: '12px',
                marginLeft: '12px',
                ...(open && { display: 'none ' }),
              }}
            >
              <ChevronRightIcon />
            </IconButton>

            {!isLargeScreen ? (
              <img
                src="/branding/e7_logo_black_round.png"
                alt="Logo"
                onClick={() => navigate('/tasks')}
                style={{ cursor: 'pointer', height: '30px', paddingLeft: '0px', marginLeft: 0 }}
              />
            ) : (
              <Box
                style={{
                  display: 'flex ',
                  justifyContent: 'center ',
                  alignItems: 'center ',
                }}
              >
                {user?.company_metadata?.logoBase64 && (
                  <Avatar
                    alt={user?.company_name}
                    src={`data:image/png;base64,${user?.company_metadata?.logoBase64}`}
                    sx={{
                      width: 36,
                      height: 36,
                      marginRight: '8px ',
                      marginLeft: '16px',
                    }}
                  />
                )}
                <Typography style={{ fontWeight: 'bold ', fontSize: '15px !important' }}>
                  {user?.company_name}
                </Typography>
              </Box>
              // </LightTooltip>
            )}
          </Box>

          {user && (
            <Box
              style={{
                display: 'flex ',
                alignItems: 'center ',
                gap: '10px ',
                marginRight: 8,
              }}
            >
              <Box
                style={{
                  display: 'flex ',
                  alignItems: 'center ',
                  gap: '10px ',
                }}
              >
                <Box style={{ padding: 2, background: 'primary ', borderRadius: 20 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'avatar.bg ',
                      width: '36px !important',
                      height: '36px !important',
                    }}
                    alt={user?.first_name}
                    src={`https://kip-orchestrator-prod.s3.ap-south-1.amazonaws.com/public-res/user-profile-image/kipl_${user?.employee_id}.jpg`}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      margin: '0 !important',
                      fontFamily: 'Plus Jakarta Sans !important',
                      fontSize: '15px !important',
                    }}
                  >
                    {user && `${user?.first_name} ${user?.last_name}`}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: '500 !important',
                      margin: '0px !important',
                      fontFamily: 'Plus Jakarta Sans !important',
                      fontSize: '11px !important',
                    }}
                  >
                    {user && user?.email}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Tooltip title="Logout">
                  <IconButton
                    onClick={() => {
                      logout()
                      navigate('/login')
                    }}
                    color="error"
                  >
                    <PowerSettingsNewOutlined />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {!user && (
            <Box>
              <Link style={{ textDecoration: 'none !important' }} variant="body2" to="/login">
                <Typography color="primary !important">Login</Typography>
              </Link>
              &nbsp;&nbsp;
              <Link style={{ textDecoration: 'none !important' }} variant="body2" to="/signup">
                <Typography color="primary !important">Sign up</Typography>
              </Link>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        className={`${classes.drawer} ${!open ? 'closed' : ''}`}
        variant="permanent"
        classes={{
          paper: `${classes.drawerPaper} ${classes.customSideMenu}`, // Apply the custom class here
        }}
        anchor="left"
        open={isLargeScreen && open}
      >
        <DrawerHeader className="drawer-header">
          <div className="logo-wrapper" onClick={() => navigate('/tasks')}>
            <img src="/branding/e7_logo_black.png" alt="Logo" className="logo-image" />
          </div>

          <IconButton
            className="Drawer-Closed-Icon drawer-hover-icon"
            onClick={() => setOpen(false)}
            color="inherit"
            sx={{ position: 'relative', mt: '-35px', right: '7px' }}
          >
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>

        <div className="epoch-sidemenu">
          <List>
            {menuItems
              .filter((item) => (item.isAdminRoute ? user.is_admin : true))
              .map((item) => {
                const hasSubMenu = item.hasSubMenu && item.subMenu && item.subMenu.length > 0
                const key = item.text // Unique key based on text

                // if (condition) {
                return (
                  <React.Fragment key={key}>
                    <ListItem
                      className={`menubar ${activeItem === item.path ? 'active' : ''}`} // Add the active class based on state
                      onClick={() => {
                        if (hasSubMenu) {
                          handleToggle(key)
                        } else {
                          navigate(item.path)
                        }
                      }}
                      style={{
                        backgroundColor:
                          location.pathname === item.path ||
                          (item.altPaths &&
                            item.altPaths.some(
                              (p) =>
                                (location.pathname + '/').includes(p) ||
                                location.pathname.includes(p)
                            ))
                            ? 'text.white'
                            : 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {item.isBadgeRequired ? (
                        <Badge
                          className="badge-bar"
                          color="primary"
                          badgeContent={badgeCounts.length > 0 ? handleBadgeCount(item.text) : null}
                          style={{
                            transform: 'translate(25px, -11px)',
                            backgroundColor: '#6C47E4',
                          }}
                        />
                      ) : null}

                      <Tooltip className="menulist" title={item.text}>
                        <ListItemIcon>{item.icon}</ListItemIcon>
                      </Tooltip>
                      <ListItemText primary={item.text} className="menutext" />

                      {hasSubMenu ? openMenus[key] ? <ExpandLess /> : <ExpandMore /> : null}
                    </ListItem>
                    {hasSubMenu && (
                      <Collapse in={openMenus[key]} timeout="auto" unmountOnExit>
                        <List component="div" style={{ cursor: 'pointer', fontSize: '12px' }}>
                          {renderTree(item.subMenu)}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                )
                // }
              })}
          </List>
        </div>
      </Drawer>

      <Box
        className={classes.page}
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          paddingTop: 0,
          paddingBottom: 1,
          marginLeft: open ? '0px' : '60px',
        }}
      >
        <Box
          className={`${classes.toolbar} ${open ? 'drawer-open' : 'drawer-closed'}`}
          padding="0px"
        ></Box>
        {children}
      </Box>
    </Box>
  )
}

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))
